import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getHasAcknowledgedSpanishPrompt,
  selectPreferredLanguage,
  setApplicationRoutingData,
  setPreferredLanguage,
} from "features/authSlice";
import { isPostLosLoan } from "features/preferredLanguageSlice";
import { useSendUserInteractedMutation, type MyLoanInfo } from "pages/application/applicationService";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { persistor } from "store";
import {
  resolveLanguageForLoanEntry,
  shouldDeferDealTranslationUntilAck,
  shouldPersistLoanEntryUserLanguage,
} from "utilities/getPreferredLanguage";
import { enterPostAuthWithLangPref } from "utilities/langPrefCookie";
import type { LoanSelectionLosType } from "utilities/loan-selection/LoanSelectionRouter";

export type NavigateToPostAuthParams = {
  loan: MyLoanInfo;
  losType: LoanSelectionLosType;
  loanKey: string;
};

export const useNavigateToPostAuth = (): ((params: NavigateToPostAuthParams) => void) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const reduxLanguage = useSelector(selectPreferredLanguage);
  const hasAcknowledgedSpanishPrompt = useSelector(getHasAcknowledgedSpanishPrompt);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const [sendUserInteracted] = useSendUserInteractedMutation();
  const isPreferredLanguageEnabled = Boolean(featureFlag?.preferredLanguageEnabled);

  return useCallback(
    ({ loan, losType, loanKey }: NavigateToPostAuthParams) => {
      const {
        nhApplicationId,
        nhApplicationStatus,
        nhDealApplicationId,
        applicationRoleType,
        dealApplicationId,
        loanId,
        hasPal,
        prospectId,
        loanPurpose,
        leadProvider,
        isConsentCollected,
        userRole,
        inquiryId,
        loanOfficerId,
        coBorrowerHasOwnNhApplication,
        nhApplicationPath,
        loanNumber,
        applicationNumber,
        userType,
        encompassLoanId,
        dealId,
        dealBorrowerId,
      } = loan;

      void sendUserInteracted({
        applicationId: dealApplicationId ?? "",
        prospectId: prospectId ?? "",
        userType,
      });

      dispatch(
        setApplicationRoutingData({
          nhApplicationId,
          nhApplicationStatus,
          nhDealApplicationId,
          applicationRoleType,
          dealApplicationId,
          loanId,
          hasPal,
          prospectId,
          loanPurpose,
          leadProvider,
          isConsentCollected,
          userRole,
          inquiryId,
          loanOfficerId,
          coBorrowerHasOwnNhApplication,
          nhApplicationPath,
          loanNumber,
          applicationNumber,
          encompassLoanId,
          losType,
          loanKey,
          dealId,
          dealBorrowerId,
        }),
      );

      const language = resolveLanguageForLoanEntry(loan, reduxLanguage, hasAcknowledgedSpanishPrompt);
      const isPostLos = isPostLosLoan(loan.loanNumber);
      const deferTranslation = shouldDeferDealTranslationUntilAck({
        showAcknowledgementModal: loan.showAcknowledgementModal,
        isPostLos,
      });

      if (
        isPreferredLanguageEnabled &&
        !deferTranslation &&
        shouldPersistLoanEntryUserLanguage(language, reduxLanguage)
      ) {
        dispatch(setPreferredLanguage(language));
      }

      void enterPostAuthWithLangPref({
        language,
        showAcknowledgementModal: loan.showAcknowledgementModal,
        isPostLos,
        isPreferredLanguageEnabled,
        spaNavigate: () => navigate("/post-auth", { replace: true }),
        persistFlush: () => persistor.flush(),
      });
    },
    [dispatch, hasAcknowledgedSpanishPrompt, isPreferredLanguageEnabled, navigate, reduxLanguage, sendUserInteracted],
  );
};
